import { useState, useEffect, useRef } from 'react';
import { goalAvailablePointsFallback } from '../../utils/goalPoints';
import useLeaderboards from '../../hooks/useLeaderboards';

/**
 * Custom hook for Dealer data loading and side effects.
 * Extracts all data-fetching functions and useEffect hooks from Dealer.js.
 */
const useDealerData = ({ user, loading, apiCall, CDN_URL, selectedTab, t }) => {
  // Company branding
  const [companyBrandingImage, setCompanyBrandingImage] = useState(null);
  const [companyBrandingColor, setCompanyBrandingColor] = useState(null);
  const [companyLanguage, setCompanyLanguage] = useState('en');
  const [companyTimezone, setCompanyTimezone] = useState('UTC');
  const [companySupportEmail, setCompanySupportEmail] = useState(null);
  const [companyDashboardWelcome, setCompanyDashboardWelcome] = useState('');
  const [companyCountryLoaded, setCompanyCountryLoaded] = useState(false);
  const [companyIsRomania, setCompanyIsRomania] = useState(false);
  const [companyIsSlovakia, setCompanyIsSlovakia] = useState(false);

  // Extra modules state
  const [companyExtraModules, setCompanyExtraModules] = useState([]);

  // Campaigns state
  const [campaigns, setCampaigns] = useState([]);
  const [loadingCampaigns, setLoadingCampaigns] = useState(false);
  const [campaignsError, setCampaignsError] = useState('');
  const [campaignsPage, setCampaignsPage] = useState(0);
  const [campaignsRowsPerPage, setCampaignsRowsPerPage] = useState(25);
  const [campaignsTotal, setCampaignsTotal] = useState(0);

  // Campaign detail view state
  const [viewingCampaignDetail, setViewingCampaignDetail] = useState(false);
  const [selectedCampaignDetail, setSelectedCampaignDetail] = useState(null);
  const [loadingCampaignDetail, setLoadingCampaignDetail] = useState(false);
  const [campaignDetailError, setCampaignDetailError] = useState('');

  // Campaign goals state
  const [campaignGoals, setCampaignGoals] = useState([]);
  const [loadingGoals, setLoadingGoals] = useState(false);
  const [goalsError, setGoalsError] = useState('');

  // Goal settings for campaign table
  const [campaignGoalSettings, setCampaignGoalSettings] = useState(null);

  // Campaign-specific quizzes state
  const [campaignQuizzes, setCampaignQuizzes] = useState([]);
  const [loadingCampaignQuizzes, setLoadingCampaignQuizzes] = useState(false);
  const [campaignQuizzesError, setCampaignQuizzesError] = useState('');

  // Marketing items state
  const [marketingItems, setMarketingItems] = useState([]);
  const [loadingMarketingItems, setLoadingMarketingItems] = useState(false);
  const [marketingItemsError, setMarketingItemsError] = useState('');

  // Dashboard state
  const [dashboardCampaigns, setDashboardCampaigns] = useState([]);
  const [loadingDashboardCampaigns, setLoadingDashboardCampaigns] = useState(false);
  const [dashboardMarketingItems, setDashboardMarketingItems] = useState([]);
  const [loadingDashboardMarketing, setLoadingDashboardMarketing] = useState(false);

  const {
    campaignsWithLeaderboards,
    leaderboardGoals,
    loadingLeaderboardGoals,
    loadingLeaderboardCampaigns,
    leaderboardSort,
    handleLeaderboardSortChange,
    getSortedLeaderboardGoals,
  } = useLeaderboards({
    apiCall,
    selectedTab,
    dealerId: user?.dealer_id,
    enabled: Boolean(user?.dealer_id),
    loading,
  });

  // Quiz state
  const [quizzes, setQuizzes] = useState([]);
  const [loadingQuizzes, setLoadingQuizzes] = useState(false);
  const [quizzesError, setQuizzesError] = useState('');

  // Bonus task state
  const [bonusTasks, setBonusTasks] = useState([]);
  const [loadingBonusTasks, setLoadingBonusTasks] = useState(false);
  const [bonusTasksError, setBonusTasksError] = useState('');

  // Formal documents acceptance
  const [showFormalDocumentsDialog, setShowFormalDocumentsDialog] = useState(false);
  const [formalDocumentsData, setFormalDocumentsData] = useState({
    terms: null,
    privacy: null,
    extra: null,
    hasTournamentModule: false
  });
  const [loadingFormalDocumentsCheck, setLoadingFormalDocumentsCheck] = useState(true);

  // ── Data loading functions ──

  const fetchCompanyBrandingImage = async (forceRefresh = false) => {
    if (!user?.company_id || !apiCall || (companyBrandingImage && !forceRefresh)) return;
    try {
      const result = await apiCall('get', `/api/companies/${user.company_id}`);
      if (result.success && result.data) {
        if (result.data.branding_image) {
          setCompanyBrandingImage(`${CDN_URL}/${result.data.branding_image}`);
        }
        if (result.data.branding_color) {
          setCompanyBrandingColor(result.data.branding_color);
        }
        const country = (result.data.country || '').trim().toLowerCase();
        const isRo = country === 'romania' || country === 'românia';
        const isSk = country === 'slovakia' || country === 'slovensko' || country === 'slovak republic';
        setCompanyIsRomania(isRo);
        setCompanyIsSlovakia(isSk);
        setCompanyCountryLoaded(true);
        let lang = 'en';
        if (country === 'poland' || country === 'polska') lang = 'pl';
        else if (country === 'hungary' || country === 'magyarország') lang = 'hu';
        else if (isSk) lang = 'sk';
        else if (isRo) lang = 'ro';
        setCompanyLanguage(lang);
        const se = (result.data.support_email && String(result.data.support_email).trim()) || '';
        setCompanySupportEmail(se || null);
        const dw = (result.data.dashboard_welcome && String(result.data.dashboard_welcome).trim()) || '';
        setCompanyDashboardWelcome(dw);
        if (result.data.timezone) {
          setCompanyTimezone(result.data.timezone);
        }
      }
    } catch (error) {
      console.error('Error fetching company branding:', error);
    }
  };

  const loadCompanyExtraModules = async () => {
    if (!user?.company_id || !apiCall) return;
    try {
      const result = await apiCall('get', `/api/company-extra-modules?company_id=${user.company_id}`);
      if (result.success) {
        setCompanyExtraModules(result.data.company_extra_modules || []);
      }
    } catch (error) {
      console.error('Error loading company extra modules:', error);
    }
  };

  const loadDealerCampaigns = async () => {
    if (!user?.dealer_id || !apiCall) return;
    setLoadingCampaigns(true);
    setCampaignsError('');
    try {
      const params = new URLSearchParams({
        page: (campaignsPage + 1).toString(),
        per_page: campaignsRowsPerPage.toString(),
        dealer_id: user.dealer_id.toString(),
        status: 'RUNNING'
      });
      const result = await apiCall('get', `/api/campaigns?${params.toString()}`);
      if (result.success) {
        const campaigns = result.data.campaigns || [];
        const campaignsWithData = await Promise.all(
          campaigns.map(async (campaign) => {
            try {
              const goalsResult = await apiCall('get', `/api/campaigns/${campaign.id}/goals`);
              if (goalsResult.success && goalsResult.data.goals && goalsResult.data.goals.length > 0) {
                const dealerGoal = goalsResult.data.goals[0];
                return {
                  ...campaign,
                  available_points: goalAvailablePointsFallback(dealerGoal),
                  value_1_sum: dealerGoal.value_1 || 0,
                  value_2_sum: dealerGoal.value_2 || 0,
                  value_3_sum: dealerGoal.value_3 || 0
                };
              } else {
                return { ...campaign, available_points: 0, value_1_sum: 0, value_2_sum: 0, value_3_sum: 0 };
              }
            } catch (error) {
              console.error(`Error loading data for campaign ${campaign.id}:`, error);
              return { ...campaign, available_points: 0, value_1_sum: 0, value_2_sum: 0, value_3_sum: 0 };
            }
          })
        );
        setCampaigns(campaignsWithData);
        setCampaignsTotal(result.data.total || 0);
        if (campaigns.length > 0) {
          const hasValue1 = campaigns.some(c => c.show_value_1);
          const hasValue2 = campaigns.some(c => c.show_value_2);
          const hasValue3 = campaigns.some(c => c.show_value_3);
          setCampaignGoalSettings({ show_value_1: hasValue1, show_value_2: hasValue2, show_value_3: hasValue3 });
        }
      } else if (!result.requiresAuth) {
        setCampaignsError(result.error || t('errors.failedLoadCampaigns'));
      }
    } catch (error) {
      console.error('Error loading campaigns:', error);
      setCampaignsError(t('errors.failedLoadCampaigns'));
    }
    setLoadingCampaigns(false);
  };

  const loadCampaignDetail = async (campaignId) => {
    if (!campaignId || !apiCall) return;
    setLoadingCampaignDetail(true);
    setCampaignDetailError('');
    try {
      const result = await apiCall('get', `/api/campaigns/${campaignId}`);
      if (result.success) {
        setSelectedCampaignDetail(result.data);
      } else if (!result.requiresAuth) {
        setCampaignDetailError(result.error || t('errors.failedLoadCampaignDetails'));
      }
    } catch (error) {
      console.error('Error loading campaign details:', error);
      setCampaignDetailError(t('errors.failedLoadCampaignDetails'));
    }
    setLoadingCampaignDetail(false);
  };

  const loadCampaignGoals = async (campaignId) => {
    if (!campaignId || !apiCall) return;
    setLoadingGoals(true);
    setGoalsError('');
    try {
      const result = await apiCall('get', `/api/campaigns/${campaignId}/goals`);
      if (result.success) {
        setCampaignGoals(result.data.goals || []);
      } else if (!result.requiresAuth) {
        setGoalsError(result.error || t('errors.failedLoadGoalData'));
      }
    } catch (error) {
      console.error('Error loading campaign goals:', error);
      setGoalsError(t('errors.failedLoadGoalData'));
    }
    setLoadingGoals(false);
  };

  const loadCampaignQuizzes = async (campaignId) => {
    if (!campaignId || !apiCall) return;
    setLoadingCampaignQuizzes(true);
    setCampaignQuizzesError('');
    try {
      const result = await apiCall('get', `/api/quizzes-with-attempts?campaign_id=${campaignId}`);
      if (result.success) {
        setCampaignQuizzes(result.data.quizzes || []);
      } else if (!result.requiresAuth) {
        setCampaignQuizzesError(result.error || t('errors.failedLoadQuizzes'));
      }
    } catch (error) {
      console.error('Error loading campaign quizzes:', error);
      setCampaignQuizzesError(t('errors.failedLoadQuizzes'));
    } finally {
      setLoadingCampaignQuizzes(false);
    }
  };

  const loadUserRewardProviderConfig = async (rewardProviderId) => {
    // This is returned so RewardsSection can use it
    if (!rewardProviderId || !apiCall) return null;
    try {
      const result = await apiCall('get', `/api/my-reward-providers/${rewardProviderId}`);
      if (result.success) {
        return result.data;
      }
    } catch (error) {
      console.error('Error loading user reward provider config:', error);
    }
    return null;
  };

  const loadMarketingItems = async () => {
    if (!user?.company_id || !apiCall) return;
    setLoadingMarketingItems(true);
    setMarketingItemsError('');
    try {
      const result = await apiCall('get', '/api/marketing-items');
      if (result.success) {
        setMarketingItems(result.data.marketing_items || []);
      } else if (!result.requiresAuth) {
        setMarketingItemsError(result.error || t('errors.failedLoadMarketingItems'));
      }
    } catch (error) {
      console.error('Error loading marketing items:', error);
      setMarketingItemsError(t('errors.failedLoadMarketingItems'));
    }
    setLoadingMarketingItems(false);
  };

  const loadDashboardCampaigns = async () => {
    if (!user?.dealer_id || !apiCall) return;
    setLoadingDashboardCampaigns(true);
    try {
      const params = new URLSearchParams({
        dealer_id: user.dealer_id.toString(), status: 'RUNNING', per_page: '5'
      });
      const result = await apiCall('get', `/api/campaigns?${params.toString()}`);
      if (result.success) {
        const campaigns = result.data.campaigns || [];
        const campaignsWithData = await Promise.all(
          campaigns.map(async (campaign) => {
            try {
              const goalsResult = await apiCall('get', `/api/campaigns/${campaign.id}/goals`);
              if (goalsResult.success && goalsResult.data.goals && goalsResult.data.goals.length > 0) {
                const dealerGoal = goalsResult.data.goals[0];
                return {
                  ...campaign,
                  available_points: goalAvailablePointsFallback(dealerGoal),
                  value_1_sum: dealerGoal.value_1 || 0,
                  value_2_sum: dealerGoal.value_2 || 0,
                  value_3_sum: dealerGoal.value_3 || 0
                };
              } else {
                return { ...campaign, available_points: 0, value_1_sum: 0, value_2_sum: 0, value_3_sum: 0 };
              }
            } catch (error) {
              console.error(`Error loading data for campaign ${campaign.id}:`, error);
              return { ...campaign, available_points: 0, value_1_sum: 0, value_2_sum: 0, value_3_sum: 0 };
            }
          })
        );
        setDashboardCampaigns(campaignsWithData);
      }
    } catch (error) {
      console.error('Error loading dashboard campaigns:', error);
    }
    setLoadingDashboardCampaigns(false);
  };

  const loadDashboardMarketing = async () => {
    if (!user?.company_id || !apiCall) return;
    setLoadingDashboardMarketing(true);
    try {
      const result = await apiCall('get', '/api/marketing-items?per_page=6');
      if (result.success) {
        setDashboardMarketingItems(result.data.marketing_items || []);
      }
    } catch (error) {
      console.error('Error loading dashboard marketing items:', error);
    }
    setLoadingDashboardMarketing(false);
  };

  const loadDashboardData = async () => {
    if (!user?.dealer_id || !user?.company_id || !apiCall) return;
    await Promise.all([loadDashboardCampaigns(), loadDashboardMarketing()]);
  };

  const loadQuizzes = async () => {
    setLoadingQuizzes(true);
    setQuizzesError('');
    try {
      const result = await apiCall('get', '/api/quizzes-with-attempts');
      if (result.success) {
        setQuizzes(result.data.quizzes || []);
      } else if (!result.requiresAuth) {
        setQuizzesError(result.error || t('errors.failedLoadQuizzes'));
      }
    } catch (error) {
      console.error('Error loading quizzes:', error);
      setQuizzesError(t('errors.failedLoadQuizzes'));
    }
    setLoadingQuizzes(false);
  };

  const loadBonusTasks = async () => {
    setLoadingBonusTasks(true);
    setBonusTasksError('');
    try {
      const result = await apiCall('get', '/api/bonus-tasks-for-dealer');
      if (result.success) {
        setBonusTasks(result.data.bonus_tasks || []);
      } else if (!result.requiresAuth) {
        setBonusTasksError(result.error || t('errors.failedLoadBonusTasks'));
      }
    } catch (error) {
      console.error('Error loading bonus tasks:', error);
      setBonusTasksError(t('errors.failedLoadBonusTasks'));
    }
    setLoadingBonusTasks(false);
  };

  // ── useEffect hooks ──

  useEffect(() => {
    if (user?.company_id && !loading && apiCall) {
      fetchCompanyBrandingImage();
      loadCompanyExtraModules();
    }
  }, [user?.company_id, loading, apiCall]);

  // Check formal documents acceptance
  useEffect(() => {
    if (user?.role !== 'Dealer' || !user?.company_id || !apiCall || loading) {
      setLoadingFormalDocumentsCheck(false);
      return;
    }
    const checkFormalDocuments = async () => {
      setLoadingFormalDocumentsCheck(true);
      try {
        const result = await apiCall('get', '/api/dealer/formal-documents-status');
        if (result.success && result.data) {
          setFormalDocumentsData({
            terms: result.data.terms || null,
            privacy: result.data.privacy || null,
            extra: result.data.extra || null,
            hasTournamentModule: result.data.has_tournament_module === true
          });
          if (result.data.needs_acceptance) {
            setShowFormalDocumentsDialog(true);
          }
        }
      } catch (err) {
        console.error('Error checking formal documents status:', err);
      }
      setLoadingFormalDocumentsCheck(false);
    };
    checkFormalDocuments();
  }, [user?.role, user?.company_id, apiCall, loading]);

  useEffect(() => {
    if (user?.dealer_id && !loading && apiCall) {
      loadQuizzes();
      loadBonusTasks();
    }
  }, [user?.dealer_id, loading, apiCall]);

  useEffect(() => {
    if (user?.dealer_id && !loading && apiCall && selectedTab === 'campaigns') {
      loadDealerCampaigns();
    }
  }, [user?.dealer_id, loading, apiCall, selectedTab, campaignsPage, campaignsRowsPerPage]);

  useEffect(() => {
    if (user?.company_id && !loading && apiCall && selectedTab === 'marketing') {
      loadMarketingItems();
    }
  }, [user?.company_id, loading, apiCall, selectedTab]);

  useEffect(() => {
    if (user?.id && !loading && apiCall && selectedTab === 'quiz') {
      loadQuizzes();
    }
  }, [user?.id, loading, apiCall, selectedTab]);

  useEffect(() => {
    if (user?.id && !loading && apiCall && selectedTab === 'bonusTask') {
      loadBonusTasks();
    }
  }, [user?.id, loading, apiCall, selectedTab]);

  useEffect(() => {
    if (user?.id && selectedTab === 'quiz' && !loadingQuizzes && quizzes.length === 0) {
      loadQuizzes();
    }
  }, [user?.id]);

  useEffect(() => {
    if (user?.dealer_id && user?.company_id && !loading && apiCall && selectedTab === 'dashboard') {
      loadDashboardData();
    }
  }, [user?.dealer_id, user?.company_id, loading, apiCall, selectedTab]);

  return {
    // Company branding
    companyBrandingImage, companyBrandingColor, companyLanguage, companyTimezone, companySupportEmail,
    companyDashboardWelcome,
    companyCountryLoaded, companyIsRomania, companyIsSlovakia,
    // Extra modules
    companyExtraModules,
    // Campaigns
    campaigns, loadingCampaigns, campaignsError, campaignsPage, setCampaignsPage,
    campaignsRowsPerPage, setCampaignsRowsPerPage, campaignsTotal,
    // Campaign detail
    viewingCampaignDetail, setViewingCampaignDetail,
    selectedCampaignDetail, setSelectedCampaignDetail,
    loadingCampaignDetail, campaignDetailError, setCampaignDetailError,
    // Campaign goals
    campaignGoals, setCampaignGoals, loadingGoals, goalsError, setGoalsError,
    campaignGoalSettings,
    // Campaign quizzes
    campaignQuizzes, setCampaignQuizzes, loadingCampaignQuizzes, campaignQuizzesError, setCampaignQuizzesError,
    // Marketing
    marketingItems, loadingMarketingItems, marketingItemsError,
    // Dashboard
    dashboardCampaigns, loadingDashboardCampaigns,
    dashboardMarketingItems, loadingDashboardMarketing,
    // Leaderboards
    campaignsWithLeaderboards, leaderboardGoals, loadingLeaderboardGoals,
    leaderboardSort, handleLeaderboardSortChange, getSortedLeaderboardGoals,
    loadingLeaderboardCampaigns,
    // Quizzes
    quizzes, loadingQuizzes, quizzesError, loadQuizzes,
    // Bonus tasks
    bonusTasks, loadingBonusTasks, bonusTasksError, loadBonusTasks,
    // Formal docs
    showFormalDocumentsDialog, setShowFormalDocumentsDialog,
    formalDocumentsData, loadingFormalDocumentsCheck,
    // Data loaders (for use in handlers)
    loadCampaignDetail, loadCampaignGoals, loadCampaignQuizzes,
    loadDealerCampaigns, loadUserRewardProviderConfig,
  };
};

export default useDealerData;
